"use client";
import { useState, useMemo, useCallback, useEffect } from "react";
import {
  Home,
  ChevronRight,
  ChevronDown,
  CornerDownRight,
  ShoppingCart,
  Settings,
  Users,
  MessageSquare,
  Globe,
  Package,
  FolderOpen,
  Archive,
  Megaphone,
  Target,
  ShoppingBag,
  FileText,
  MapPin,
  Cog,
  Wallet,
  Tag,
} from "lucide-react";

import { Link } from "react-router-dom";
const navItems: NavBarItem[] = [
  {
    icon: <Home className="w-4" />,
    label: "Dashboard",
    href: "./dashboard",
    children: [],
  },
  {
    icon: <Package className="w-4" />,
    label: "Products",
    href: "./product",
    children: [
      {
        icon: <Package className="w-4" />,
        label: "Products",
        href: "./product",
        children: [],
      },
      {
        icon: <FolderOpen className="w-4" />,
        label: "Collections",
        href: "./product/collection",
        children: [],
      },
      {
        icon: <Archive className="w-4" />,
        label: "Inventory",
        href: "./product/inventory",
        children: [],
      },
    ],
  },
  {
    icon: <ShoppingCart className="w-4" />,
    label: "Orders",
    href: "./orders",
    children: [],
  },
  {
    icon: <Users className="w-4" />,
    label: "Customers",
    href: "./customers",
    children: [],
  },
  {
    icon: <Tag className="w-4" />,
    label: "Coupons",
    href: "./coupons",
    children: [],
  },
  {
    icon: <Wallet className="w-4" />,
    label: "Wallet",
    href: "./wallet",
    children: [],
  },
  {
    icon: <MessageSquare className="w-4" />,
    label: "Tickets",
    href: "./tickets",
    children: [],
  },
  {
    icon: <Megaphone className="w-4" />,
    label: "Marketing",
    href: "./marketing",
    children: [
      {
        icon: <Target className="w-4" />,
        label: "Automation",
        href: "./marketing/automation",
        children: [],
      },
      {
        icon: <Megaphone className="w-4" />,
        label: "Campaign",
        href: "./marketing/campaign",
        children: [],
      },
      {
        icon: <ShoppingBag className="w-4" />,
        label: "Abandoned Cart",
        href: "./marketing/abandoned-cart",
        children: [],
      },
      {
        icon: <FileText className="w-4" />,
        label: "Templates",
        href: "./marketing/templates",
        children: [],
      },
    ],
  },
  {
    icon: <FolderOpen className="w-4" />,
    label: "Assets",
    href: "./files/manage",
    children: [],
  },
  {
    icon: <Globe className="w-4" />,
    label: "Web Builder",
    href: "./webbuilder",
    children: [],
  },
  {
    icon: <Settings className="w-4" />,
    label: "Settings",
    href: "./settings",
    children: [
      {
        icon: <Settings className="w-4" />,
        label: "General",
        href: "./settings",
        children: [],
      },
      {
        icon: <MapPin className="w-4" />,
        label: "Locations",
        href: "./settings/locations",
        children: [],
      },
      {
        icon: <Cog className="w-4" />,
        label: "Configuration",
        href: "./settings/configuration",
        children: [],
      },
      // {
      //   icon: <CreditCard className="w-4" />,
      //   label: "Subscription",
      //   href: "./settings/subscription",
      //   children: [],
      // },
    ],
  },
];
import { useLocation } from "react-router-dom";
import { NavBarItem } from "@/types/common";
import { usePermissions } from "@/hooks/usePermissions";
import { whitelabelConfig } from "@/config/whitelabel";

export default function Sidebar() {
  const location = useLocation();
  const pathname = location.pathname;
  const appVersion = import.meta.env.VITE_APP_VERSION || "0.0.0";
  const [collapsed, setCollapsed] = useState<boolean>(false);
  const [isAutoCollapsed, setIsAutoCollapsed] = useState<boolean>(false);
  const [showOverlay, setShowOverlay] = useState<boolean>(false);
  const [overlayOpen, setOverlayOpen] = useState<boolean>(false);
  const { currentStore } = usePermissions();

  // Toggle animated overlay state when showOverlay changes
  useEffect(() => {
    if (showOverlay) {
      // allow next tick for transition to pick up
      requestAnimationFrame(() => setOverlayOpen(true));
    } else {
      setOverlayOpen(false);
    }
  }, [showOverlay]);

  // Handle responsive sidebar collapse
  useEffect(() => {
    const handleResize = () => {
      const shouldAutoCollapse = window.innerWidth < 1400;
      setIsAutoCollapsed(shouldAutoCollapse);

      // Auto-collapse when screen is too small
      if (shouldAutoCollapse) {
        setCollapsed(true);
      }
    };

    // Check on mount
    handleResize();

    // Add event listener
    window.addEventListener("resize", handleResize);

    // Cleanup
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Extract store context from current URL - memoized to prevent re-creation
  const storeBasePath = useMemo(() => {
    if (!pathname) return "";
    const pathSegments = pathname?.split("/").filter(Boolean);
    return pathSegments.length >= 2
      ? `/${pathSegments[0]}/${pathSegments[1]}`
      : "";
  }, [pathname]);

  // Convert relative paths to absolute paths
  const convertToAbsolutePath = useCallback(
    (href: string): string => {
      if (href.startsWith("./")) {
        // If we're already inside a store path, use that base. Otherwise
        // fall back to the currentStore from permissions so links still
        // resolve when rendered outside a store-scoped route.
        if (storeBasePath) return `${storeBasePath}/${href.substring(2)}`;
        if (currentStore && currentStore.storeType && currentStore.storeId) {
          return `/${currentStore.storeType}/${currentStore.storeId}/${href.substring(2)}`;
        }

        return `/${whitelabelConfig.storeType}/${whitelabelConfig.storeId}/${href.substring(2)}`;
      }
      return href;
    },
    [storeBasePath, currentStore],
  );

  // Create nav items with absolute paths - memoized
  const filteredNavItems = useMemo(() => {
    const createAbsoluteNavItems = (items: NavBarItem[]): NavBarItem[] => {
      return items.map((item) => ({
        ...item,
        href: convertToAbsolutePath(item.href),
        children: createAbsoluteNavItems(item.children || []),
      }));
    };
    // Use the top-level `navItems` as the source for conversion
    const result = createAbsoluteNavItems(navItems);
    return result;
  }, [convertToAbsolutePath, currentStore]);

  return (
    <>
      {/* Main Sidebar */}
      <div
        className={`h-screen bg-card transition-[width] duration-600 flex flex-col shadow-theme-sidebar ${
          collapsed ? "w-[60px]" : "w-[270px]"
        }`}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between px-4 py-4 bg-primary h-16">
          <span
            className={`${
              collapsed ? "hidden" : ""
            } transition-all duration-1000 w-9/12`}
          >
            <img src={whitelabelConfig.logos.app} alt={`${whitelabelConfig.brandName} logo`} />
          </span>

          <button
            onClick={() => {
              if (isAutoCollapsed && collapsed) {
                // Show overlay instead of expanding when screen is too small
                setShowOverlay(true);
              } else {
                setCollapsed(!collapsed);
              }
            }}
            className="cursor-pointer"
          >
            <img
              src="/assets/nav.svg"
              alt="navbar icon"
              className={`${collapsed ? "rotate-180" : ""}`}
            />
          </button>
        </div>

        {/* Nav Items */}
        <nav className="flex-1 mt-4 overflow-y-auto">
          {filteredNavItems.map((item, index) => (
            <div key={index}>
              <Link
                to={item.href}
                className={`flex items-center cursor-pointer transition-all duration-700 border border-border rounded-full ${collapsed ? "border-0 m-2 p-2 py-2 justify-center" : "mx-4 my-2 px-4 py-2"} hover:bg-primary hover:text-primary-foreground hover:shadow-lg ${pathname === item.href || pathname?.startsWith(item.href + "/") ? "bg-primary text-primary-foreground shadow-lg" : ""}`}
              >
                {item.icon}
                <span
                  className={`${
                    collapsed ? "w-0 hidden" : "pl-4"
                  } flex-1 overflow-hidden flex`}
                >
                  <span className="flex-1">{item.label}</span>
                  {pathname === item.href ||
                  pathname?.startsWith(item.href + "/") ? (
                    <ChevronDown className="w-4" />
                  ) : (
                    <ChevronRight className="w-4" />
                  )}
                </span>
              </Link>
              {item.children &&
                !collapsed &&
                (pathname === item.href ||
                  pathname?.startsWith(item.href + "/")) && (
                  <div className="pl-12">
                    {item.children.map((childItem, childIndex) => (
                      <div className="py-2 flex" key={childIndex}>
                        <span className="w-6 mr-2">
                          <CornerDownRight
                            className={`${pathname === childItem.href ? "" : "hidden"}`}
                          />
                        </span>
                        <Link
                          to={childItem.href}
                          className={`${pathname === childItem.href ? "text-primary font-semibold" : ""}`}
                        >
                          <span>{childItem.label}</span>
                        </Link>
                      </div>
                    ))}
                  </div>
                )}
            </div>
          ))}
        </nav>

        {!collapsed && (
          <div className="px-4 py-3 border-t border-border text-xs text-muted-foreground text-center">
            Version {appVersion}
          </div>
        )}
      </div>

      {/* Overlay for small screens */}
      {showOverlay && (
        <div
          className="fixed inset-0 z-[9999]"
          onClick={() => {
            // animate close
            setOverlayOpen(false);
            setTimeout(() => setShowOverlay(false), 300);
          }}
        >
          {/* Backdrop */}
          <div
            className={`fixed inset-0 z-[9999] bg-black/40 transition-opacity duration-300 ${overlayOpen ? "opacity-100" : "opacity-0"}`}
          />

          {/* Sliding panel */}
          <div
            className={`absolute left-0 top-0 bottom-0 z-[10000] bg-card w-64 max-w-full shadow-theme-sidebar transform transition-transform duration-300 ease-out flex flex-col ${overlayOpen ? "translate-x-0" : "-translate-x-full"}`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header inside overlay */}
            <div className="sticky top-0 z-10 flex items-center justify-between px-4 py-4 bg-primary h-16">
              <span className="transition-all duration-1000 w-9/12">
                <img src={whitelabelConfig.logos.app} alt={`${whitelabelConfig.brandName} logo`} />
              </span>
              <button
                onClick={() => {
                  setOverlayOpen(false);
                  setTimeout(() => setShowOverlay(false), 300);
                }}
                className="cursor-pointer"
              >
                <img src="/assets/nav.svg" alt="close" className="rotate-180" />
              </button>
            </div>

            {/* Nav Items (reuse same nav list) */}
            <nav className="flex-1 mt-4 overflow-y-auto">
              {filteredNavItems.map((item, index) => (
                <div key={index}>
                  <Link
                    to={item.href}
                    onClick={() => {
                      setOverlayOpen(false);
                      setTimeout(() => setShowOverlay(false), 300);
                    }}
                    className={`flex items-center cursor-pointer transition-all duration-700 border border-border rounded-full mx-4 my-2 px-4 py-2 hover:bg-primary hover:text-primary-foreground hover:shadow-lg ${pathname === item.href || pathname?.startsWith(item.href + "/") ? "bg-primary text-primary-foreground shadow-lg" : ""}`}
                  >
                    {item.icon}
                    <span className="pl-4 flex-1 overflow-hidden flex">
                      <span className="flex-1">{item.label}</span>
                      {pathname === item.href ||
                      pathname?.startsWith(item.href + "/") ? (
                        <ChevronDown className="w-4" />
                      ) : (
                        <ChevronRight className="w-4" />
                      )}
                    </span>
                  </Link>
                  {item.children &&
                    (pathname === item.href ||
                      pathname?.startsWith(item.href + "/")) && (
                      <div className="pl-12">
                        {item.children.map((childItem, childIndex) => (
                          <div className="py-2 flex" key={childIndex}>
                            <span className="w-6 mr-2">
                              <CornerDownRight
                                className={`${pathname === childItem.href ? "" : "hidden"}`}
                              />
                            </span>
                            <Link
                              to={childItem.href}
                              onClick={() => {
                                setOverlayOpen(false);
                                setTimeout(() => setShowOverlay(false), 300);
                              }}
                              className={`${pathname === childItem.href ? "text-primary font-semibold" : ""}`}
                            >
                              <span>{childItem.label}</span>
                            </Link>
                          </div>
                        ))}
                      </div>
                    )}
                </div>
              ))}
            </nav>

            <div className="px-4 py-3 border-t border-border text-xs text-muted-foreground text-center">
              Version {appVersion}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
